import React from 'react';
import { TeddyBear } from '@withjoy/joykit/icons';
import { ButtonV2, FormControl, SpacingStack, TextV2 } from '@withjoy/joykit';
import { EventInputV2, EventCheckboxV2, EventDatePickerInputV2 } from '@shared/createEvent/formElements';
import { EventDetailsContainer } from '@shared/createEvent/EventDetailsContainer/EventDetailsContainer';

import { StyledForm } from './CreateBabyRegistryEvent.styles';
import { useCreateJoyEventWrapperController } from './CreateBabyRegistryEvent.controller';
import { wrapWithCreateBabyRegistryEventHealthProvider } from './CreateBabyRegistryEvent.health';

const LOTTIE_URL = 'https://withjoy.com/assets/public/apps/registry/lottie/create_registry_confetti.json';

export interface CreateBabyRegistryEventProps {}

export const CreateBabyRegistryEvent: React.FC<CreateBabyRegistryEventProps> = wrapWithCreateBabyRegistryEventHealthProvider(() => {
  const {
    t,
    submitButtonRef,
    formik,
    telemetry,
    blockEventDate,
    eventFormTranslations,
    getFieldErrorProps,
    handleDateChange,
    handleBlockDate
  } = useCreateJoyEventWrapperController();

  const heroText = t('heroText')();
  const helpText = t('helpText')({
    helpChangeDetails: () => (
      <TextV2 as="span" typographyVariant="body3" color="mono14" fontSize={18} display="block">
        {t('helpChangeDetails')()}
      </TextV2>
    )
  });

  return (
    <EventDetailsContainer
      lottie={{ lottieUrl: LOTTIE_URL, play: true }}
      header={{ title: heroText, subTitle: helpText, icon: <TeddyBear color="mono14" fill="#333333" size={48} /> }}
      paddingTop={56}
      paddingBottom={56}
    >
      <StyledForm onSubmit={formik.handleSubmit}>
        <SpacingStack spacing={9} width={['100%', 336]}>
          <SpacingStack spacing={6}>
            <FormControl
              onFocus={telemetry.registryOwnerFirstNameClicked}
              {...getFieldErrorProps('ownerFirstName')}
              label={
                <TextV2 typographyVariant="hed1" marginBottom={4}>
                  {eventFormTranslations.ownerFirstName()}
                </TextV2>
              }
            >
              <EventInputV2 placeholder={eventFormTranslations.ownerFirstNamePlaceholder()} {...formik.getFieldProps('ownerFirstName')} {...getFieldErrorProps('ownerFirstName')} />
            </FormControl>
            <FormControl
              onFocus={telemetry.registryOwnerLastNameClicked}
              {...getFieldErrorProps('ownerLastName')}
              label={
                <TextV2 typographyVariant="hed1" marginBottom={4}>
                  {eventFormTranslations.ownerLastName()}
                </TextV2>
              }
            >
              <EventInputV2 placeholder={eventFormTranslations.ownerLastNamePlaceholder()} {...formik.getFieldProps('ownerLastName')} {...getFieldErrorProps('ownerLastName')} />
            </FormControl>
            <SpacingStack spacing={5}>
              <FormControl
                {...getFieldErrorProps('eventDate')}
                label={
                  <TextV2 typographyVariant="hed1" marginBottom={4}>
                    {eventFormTranslations.babyArrivalDate()}
                  </TextV2>
                }
              >
                <EventDatePickerInputV2 {...formik.getFieldProps('eventDate')} {...getFieldErrorProps('eventDate')} onChange={handleDateChange} />
              </FormControl>
              <EventCheckboxV2 {...formik.getFieldProps('blockEventDate')} checked={blockEventDate} isChecked={blockEventDate} onChange={handleBlockDate}>
                <TextV2 as="p" typographyVariant="button2" color="mono14" fontWeight={400}>
                  {eventFormTranslations.skipDateDefinition()}
                </TextV2>
              </EventCheckboxV2>
            </SpacingStack>
          </SpacingStack>
          <ButtonV2 ref={submitButtonRef} marginX={6} loading={formik.isSubmitting} type="submit" onClick={formik.submitForm} intent="primary" borderRadius={64} height={10}>
            {eventFormTranslations.createEventCta()}
          </ButtonV2>
        </SpacingStack>
      </StyledForm>
    </EventDetailsContainer>
  );
});
